import { format, parseISO, isValid } from "date-fns";

// Standard date filter options used across the app
export const DATE_FILTER_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "this_year", label: "This Year" },
  { value: "last_year", label: "Last Year" },
  { value: "all", label: "All Time" },
  { value: "custom", label: "Custom Range…" },
];

// Default date filter value
export const DEFAULT_DATE_FILTER = "today";

/**
 * Compute the [start, end] date range (inclusive) for a given filter value.
 * End is the last day of the range (not exclusive). Returns { start: Date, end: Date } or null for "all".
 * Uses the user's LOCAL timezone (browser default).
 */
export const getDateRangeForFilter = (filterValue, customStart = null, customEnd = null) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const startOfWeek = (d) => {
    const nd = new Date(d);
    const day = nd.getDay(); // 0 Sun, 1 Mon, ...
    const diff = day === 0 ? -6 : 1 - day; // move to Monday
    nd.setDate(nd.getDate() + diff);
    nd.setHours(0, 0, 0, 0);
    return nd;
  };

  switch (filterValue) {
    case "today": {
      return { start: today, end: today };
    }
    case "yesterday": {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      return { start: y, end: y };
    }
    case "this_week": {
      const s = startOfWeek(today);
      return { start: s, end: today };
    }
    case "last_week": {
      const thisWeek = startOfWeek(today);
      const s = new Date(thisWeek);
      s.setDate(s.getDate() - 7);
      const e = new Date(thisWeek);
      e.setDate(e.getDate() - 1);
      return { start: s, end: e };
    }
    case "this_month": {
      const s = new Date(today.getFullYear(), today.getMonth(), 1);
      return { start: s, end: today };
    }
    case "last_month": {
      const s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const e = new Date(today.getFullYear(), today.getMonth(), 0);
      return { start: s, end: e };
    }
    case "this_year": {
      const s = new Date(today.getFullYear(), 0, 1);
      return { start: s, end: today };
    }
    case "last_year": {
      const s = new Date(today.getFullYear() - 1, 0, 1);
      const e = new Date(today.getFullYear() - 1, 11, 31);
      return { start: s, end: e };
    }
    case "custom": {
      if (customStart && customEnd) {
        const s = new Date(customStart + "T00:00:00");
        const e = new Date(customEnd + "T00:00:00");
        return { start: s, end: e };
      }
      return null;
    }
    case "all":
    default:
      return null;
  }
};

/**
 * Format a date filter value (or custom range) as a human-readable range string.
 * Example: "Apr 13 – Apr 19, 2026" or "Apr 20, 2026"
 */
export const formatDateFilterRange = (filterValue, customStart = null, customEnd = null) => {
  if (filterValue === "all") return "All time";
  const range = getDateRangeForFilter(filterValue, customStart, customEnd);
  if (!range) return "";
  const { start, end } = range;
  if (start.getTime() === end.getTime()) {
    return formatDate(start);
  }
  const sameYear = start.getFullYear() === end.getFullYear();
  const startStr = sameYear ? formatDate(start, "MMM d") : formatDate(start);
  const endStr = formatDate(end);
  return `${startStr} – ${endStr}`;
};

/**
 * Convert a Date to "YYYY-MM-DD" (local) for API calls and <input type="date">.
 */
export const toYMD = (d) => {
  if (!d) return "";
  const dt = d instanceof Date ? d : new Date(d);
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};

/**
 * Convert a Date to "YYYY-MM-DD" in a SPECIFIC IANA timezone.
 *
 * Use this when the value represents "what calendar day is it FOR THE
 * BUSINESS right now" — independent of the browser's timezone. Example:
 *   business tz = "America/New_York"
 *   browser tz  = "America/Los_Angeles"
 *   actual UTC  = 2026-05-12T05:30Z  (11:30 PM PT = 2:30 AM ET next day)
 *   toYMD(new Date())                  → "2026-05-11"  (Pacific date, wrong)
 *   toYMDInTimezone(new Date(), "America/New_York") → "2026-05-12"  (correct)
 *
 * Falls back to `toYMD` if the timezone is missing or invalid so the
 * caller never gets an empty string.
 */
export const toYMDInTimezone = (d, tz) => {
  if (!d) return "";
  if (!tz) return toYMD(d);
  const dt = d instanceof Date ? d : new Date(d);
  try {
    // en-CA locale gives ISO-style YYYY-MM-DD straight out of `formatToParts`
    // / `format` so we don't have to reorder the parts ourselves.
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(dt);
    const get = (type) => parts.find((p) => p.type === type)?.value;
    const y = get("year");
    const m = get("month");
    const day = get("day");
    if (y && m && day) return `${y}-${m}-${day}`;
  } catch {
    // Invalid timezone — fall through to local.
  }
  return toYMD(dt);
};

/**
 * Format a date string or Date object to a readable format
 * @param {string|Date} date - Date to format
 * @param {string} formatStr - date-fns format string (default: "MMM d, yyyy")
 * @returns {string} Formatted date string
 */
export const formatDate = (date, formatStr = "MMM d, yyyy") => {
  if (!date) return "-";
  
  try {
    let dateObj;
    
    if (typeof date === "string") {
      // Handle various date formats
      // If it's just a date like "2025-01-22", add time to make it valid
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        dateObj = new Date(date + "T00:00:00");
      } else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(date)) {
        // Handle MM/DD/YYYY format
        const [month, day, year] = date.split('/');
        dateObj = new Date(year, month - 1, day);
      } else {
        dateObj = parseISO(date);
      }
    } else {
      dateObj = date;
    }
    
    if (!isValid(dateObj)) return date; // Return original if can't parse
    return format(dateObj, formatStr);
  } catch {
    return date || "-"; // Return original date string if formatting fails
  }
};

/**
 * Format a date with time
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (date) => {
  return formatDate(date, "MMM d, yyyy 'at' h:mm a");
};

/**
 * Format time only
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted time string
 */
export const formatTime = (date) => {
  return formatDate(date, "h:mm a");
};

/**
 * Format date for display in job cards, lists, etc.
 * Shows day of week for better context
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date with day of week
 */
export const formatDateWithDay = (date) => {
  return formatDate(date, "EEE, MMM d, yyyy");
};

/**
 * Format date in short form for compact displays
 * @param {string|Date} date - Date to format
 * @returns {string} Short formatted date
 */
export const formatDateShort = (date) => {
  return formatDate(date, "MMM d");
};

/**
 * Convert a scheduled_time string from 24h (e.g. "14:00") to 12h (e.g. "2:00 PM").
 * Also handles times already in 12h format (e.g. "2:00 PM") by passing them through.
 * @param {string} timeStr - Time string in "HH:mm" or "h:mm a" format
 * @returns {string} Formatted 12-hour time string, or empty string if unparseable
 */
export const formatScheduledTime = (timeStr) => {
  if (!timeStr || !timeStr.trim() || timeStr.trim().toLowerCase() === "none") return "";
  const trimmed = timeStr.trim();
  // Already in 12h format (contains AM/PM)
  if (/[ap]m/i.test(trimmed)) return trimmed;
  // Parse 24h "HH:mm" format
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (match) {
    let h = parseInt(match[1], 10);
    const m = match[2];
    const ampm = h >= 12 ? "PM" : "AM";
    if (h === 0) h = 12;
    else if (h > 12) h -= 12;
    return `${h}:${m} ${ampm}`;
  }
  return trimmed;
};

/**
 * Extract time from a scheduled_date ISO string and format to 12h.
 * Returns empty string if date is midnight (no real time set).
 */
export const extractTimeFromDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    const h = d.getHours();
    const m = d.getMinutes();
    // Skip midnight (00:00) - likely means no specific time was set
    if (h === 0 && m === 0) return "";
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
  } catch {
    return "";
  }
};

/**
 * Get label for date filter value
 * @param {string} filterValue - The filter value
 * @returns {string} Human readable label
 */
export const getDateFilterLabel = (filterValue) => {
  const option = DATE_FILTER_OPTIONS.find(opt => opt.value === filterValue);
  return option ? option.label : filterValue;
};

/**
 * Format the full schedule display for a job — supports Between-Hours windows.
 * Exact time → "2:00 PM" · Between hours → "Between 2:00 PM and 4:00 PM".
 * Falls back to the time embedded in scheduled_date when scheduled_time is empty.
 */
export const formatJobScheduleDisplay = (job) => {
  if (!job) return null;
  const start = formatScheduledTime(job.scheduled_time) || extractTimeFromDate(job.scheduled_date);
  const end = formatScheduledTime(job.scheduled_time_end);
  if (start && end && job.schedule_type !== "exact") {
    return `Between ${start} and ${end}`;
  }
  return start || null;
};

/**
 * Add fractional hours to a time string, wrapping past midnight.
 * Preserves the input style: "1:00 PM" → "3:00 PM", "23:00" → "01:00".
 * Returns "" when the time can't be parsed.
 */
export const addHoursToTime = (timeStr, hours) => {
  const m = String(timeStr || "").trim().match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/i);
  if (!m) return "";
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const mer = m[3] ? m[3].toLowerCase() : null;
  if (mer === "pm" && h !== 12) h += 12;
  if (mer === "am" && h === 12) h = 0;
  let total = (h * 60 + min + Math.round((parseFloat(hours) || 0) * 60)) % 1440;
  if (total < 0) total += 1440;
  const oh = Math.floor(total / 60);
  const om = total % 60;
  if (mer) {
    const outMer = oh >= 12 ? "PM" : "AM";
    const h12 = oh % 12 === 0 ? 12 : oh % 12;
    return `${h12}:${String(om).padStart(2, "0")} ${outMer}`;
  }
  return `${String(oh).padStart(2, "0")}:${String(om).padStart(2, "0")}`;
};
